// 定义HTML实体映射类型
interface HtmlEntityMap {
  [key: string]: string;
}

// HTML实体映射表
const HTML_ENTITY_MAP: HtmlEntityMap = {
  '&lt;': '<',
  '&amp;': '&',
  '&quot;': '"',
  '&ldquot;': '"',
  '&rdquot;': '"',
  '&ldquo;': '"',
  '&rdquo;': '"',
  '&gt;': '>',
  '&#39;': "'",
  '&nbsp;': ' ',
  '&mdash;': '-',
  '&ensp;': ' ', // 半角空格
  '&emsp;': '  ', // 全角空格
  '&copy;': '©',
  '&reg;': '®',
  '&trade;': '™'
}

/**
 * 处理HTML内容中的特殊字符和图片样式
 * @param content - 要处理的HTML内容字符串
 * @returns 处理后的HTML内容字符串
 */
export function handleHtmlContent (content: string): string {
  if (!content || typeof content !== 'string') {
    return content || ''
  }

  let processedContent = content

  // 处理HTML实体编码
  processedContent = processedContent.replace(
    /&(?:amp|gt|lt|quot|ldquot|rdquot|ldquo|rdquo|nbsp|mdash|ensp|emsp|copy|reg|trade|#39);/g,
    (entity: string): string => {
      return HTML_ENTITY_MAP[entity] || entity
    }
  )

  // 处理数字实体编码（如 &#160;）
  processedContent = processedContent.replace(
    /&#(\d+);/g,
    (match: string, code: string): string => {
      return String.fromCharCode(parseInt(code, 10))
    }
  )

  // 处理十六进制实体编码（如 &#xA0;）
  processedContent = processedContent.replace(
    /&#x([0-9A-Fa-f]+);/g,
    (match: string, hexCode: string): string => {
      return String.fromCharCode(parseInt(hexCode, 16))
    }
  )

  // 处理图片样式，防止变形
  processedContent = processedContent.replace(
    /<img(\s[^>]*)?>/gi,
    (imgTag: string): string => {
      // 检查是否已经有style属性
      if (imgTag.includes('style=')) {
        // 如果已有style属性，添加width和height约束
        return imgTag.replace(
          /style=(["'])([^"']*)\1/i,
          (match: string, quote: string, existingStyle: string): string => {
            // 确保样式包含宽度和高度约束
            let newStyle = existingStyle
            if (!existingStyle.includes('width')) {
              newStyle += ';width: 100%'
            }
            if (!existingStyle.includes('height')) {
              newStyle += ';height: auto'
            }
            if (!existingStyle.includes('max-width')) {
              newStyle += ';max-width: 100%'
            }
            return `style=${quote}${newStyle}${quote}`
          }
        )
      } else {
        // 如果没有style属性，添加完整的样式
        return imgTag.replace(
          /<img(\s[^>]*)?>/i,
          (match: string, attributes: string): string => {
            return `<img style="width: 100%; height: auto; max-width: 100%; display: block;"${attributes || ''}>`
          }
        )
      }
    }
  )

  // 确保图片标签正确闭合
  processedContent = processedContent.replace(
    /<img([^>]+)(?<!\/)>/gi,
    '<img$1/>'
  )

  // 处理常见的图片格式自闭合问题
  processedContent = processedContent.replace(
    /<img([^>]*?)\.(png|jpg|jpeg|gif|webp)">/gi,
    '<img$1.$2"/>'
  )

  return processedContent
}

/**
 * 更高级的HTML内容处理选项
 */
interface HtmlContentOptions {
  /** 是否解码HTML实体 */
  decodeEntities?: boolean;
  /** 是否修复图片样式 */
  fixImages?: boolean;
  /** 图片最大宽度 */
  maxImageWidth?: string;
  /** 是否确保图片标签闭合 */
  ensureImgClosed?: boolean;
  /** 自定义实体映射 */
  customEntityMap?: HtmlEntityMap;
}

/**
 * 高级HTML内容处理函数
 * @param content - 要处理的HTML内容字符串
 * @param options - 处理选项
 * @returns 处理后的HTML内容字符串
 */
export function handleHtmlContentAdvanced (
  content: string,
  options: HtmlContentOptions = {}
): string {
  const {
    decodeEntities = true,
    fixImages = true,
    maxImageWidth = '100%',
    ensureImgClosed = true,
    customEntityMap = {}
  } = options

  if (!content || typeof content !== 'string') {
    return content || ''
  }

  let processedContent = content

  // 合并自定义实体映射
  const entityMap = { ...HTML_ENTITY_MAP, ...customEntityMap }

  // 解码HTML实体
  if (decodeEntities) {
    // 处理命名实体
    processedContent = processedContent.replace(
      /&(?:[a-z]+|#\d+|#x[0-9a-f]+);/gi,
      (entity: string): string => {
        // 优先使用自定义映射
        if (entityMap[entity]) {
          return entityMap[entity]
        }

        // 处理数字实体
        const numericMatch = entity.match(/^&#(\d+);$/i)
        if (numericMatch) {
          return String.fromCharCode(parseInt(numericMatch[1], 10))
        }

        // 处理十六进制实体
        const hexMatch = entity.match(/^&#x([0-9a-f]+);$/i)
        if (hexMatch) {
          return String.fromCharCode(parseInt(hexMatch[1], 16))
        }

        // 未知实体返回原样
        return entity
      }
    )
  }

  // 修复图片样式
  if (fixImages) {
    processedContent = processedContent.replace(
      /<img(\s[^>]*)?>/gi,
      (imgTag: string): string => {
        const styleMatch = imgTag.match(/style=(["'])([^"']*)\1/i)

        if (styleMatch) {
          // 更新现有样式
          let style = styleMatch[2]

          // 添加或更新宽度设置
          style = style.replace(/(^|;)\s*width\s*:[^;]*/i, '')
          style += `;width: ${maxImageWidth}`

          // 添加或更新高度设置
          style = style.replace(/(^|;)\s*height\s*:[^;]*/i, '')
          style += ';height: auto'

          // 添加最大宽度
          style = style.replace(/(^|;)\s*max-width\s*:[^;]*/i, '')
          style += `;max-width: ${maxImageWidth}`

          // 确保图片块级显示
          if (!style.includes('display')) {
            style += ';display: block'
          }

          // 清理多余的分号
          style = style.replace(/;+/g, ';').replace(/^;|;$/g, '')

          return imgTag.replace(styleMatch[0], `style=${styleMatch[1]}${style}${styleMatch[1]}`)
        } else {
          // 添加新样式
          const attributes = imgTag.match(/<img(\s[^>]*)?>/i)?.[1] || ''
          return `<img style="width: ${maxImageWidth}; height: auto; max-width: ${maxImageWidth}; display: block;"${attributes}>`
        }
      }
    )
  }

  // 确保图片标签正确闭合
  if (ensureImgClosed) {
    processedContent = processedContent.replace(
      /<img([^>]+)(?<!\/)\s*>/gi,
      '<img$1/>'
    )
  }

  return processedContent
}

// 导出类型
export type { HtmlEntityMap, HtmlContentOptions }
